import * as fuzzysort from "fuzzysort";
import React, { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import { BottomBarDouble } from "~/src/components/BottomBars/BottomBars";
import BottomLine from "~/src/components/BottomLine";
import { CancelIcon } from "~/src/components/Icons/Icons";
import Text from "~/src/components/Text/Text";
import { StorageContext } from "~/src/contexts/StorageContext/StorageProvider";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import { Favorittområde, Område, OmrådeData, Områdekode } from "../../api/types";
import { jaktdataApi } from "../../appGlobals";
import { ArtButtons } from "../../components/Button/ArtButtons";
import ButtonDefault, { ButtonVariant } from "../../components/Button/ButtonDefault";
import InputFilterDefault from "../../components/Form/Input/InputFilterDefault";
import JaktområdeDefault from "../../components/Jaktomrade/Jaktomrade";
import AppDataContext from "../../contexts/AppDataContext/DataProvider";
import { harJaktPaArt } from "../../helpers/filters";
import { ArtType } from "../../helpers/types";
import { useStateful } from "../../hooks/useStateful";

export default function (): JSX.Element {
  const { t, i18n } = useTranslation();
  const art = useStateful<ArtType | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const appData = React.useContext(AppDataContext);
  const navigate = useNavigate();
  const områdeData = useStateful<OmrådeData | undefined>(undefined);
  const filteredData = useStateful<Område[]>([]);
  const filterValue = useStateful<string>("");
  const valgteOmråder = useStateful<Områdekode[]>([]);
  const isSending = useStateful<boolean>(false);
  const ui = useContext(UIContext);
  const storage = useContext(StorageContext);

  const maxItems = 25;

  const hasMatchingFilters = (entry: Område) => {
    const navnOrIdMatch =
      entry.Navn?.toLowerCase().includes(filterValue.value.toLowerCase()) ||
      entry.Id?.toLowerCase().includes(filterValue.value.toLowerCase());
    let typeSpecificMatch = false;
    if ("ValdId" in entry) {
      typeSpecificMatch = entry.ValdId!.includes(filterValue.value.toLowerCase());
    }
    return navnOrIdMatch || typeSpecificMatch;
  };

  useEffect(() => {
    switch (art.value) {
      case ArtType.Elg:
      case ArtType.Hjort:
        områdeData.set(appData.jaktfelt.data);
        break;
      case ArtType.Rådyr:
        områdeData.set(appData.vald.data);
        break;
      case ArtType.Villrein:
        områdeData.set(appData.villreinvald.data);
        break;
      default:
        områdeData.set(undefined);
    }
  }, [
    art.value,
    områdeData.set,
    appData.jaktfelt.data,
    appData.vald.data,
    appData.villreinvald.data,
  ]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.click();
    }
  }, [art.value]);

  useEffect(() => {
    if (områdeData.value !== undefined && områdeData.value.data !== undefined) {
      const områdeDataFiltered = områdeData.value.data.filter((entry: Område) => {
        return harJaktPaArt(art.value, entry) && hasMatchingFilters(entry);
      });
      filteredData.set(
        fuzzysort
          .go(filterValue.value, områdeDataFiltered, { keys: ["Id", "Navn", "ValdId"] })
          .map((result) => {
            return result.obj;
          })
          .slice(0, maxItems),
      );
    }
  }, [områdeData.value, filterValue.value, art.value, filteredData.set]);

  const områdeEntry = (områdeEntry: Område) => {
    return (
      <JaktområdeDefault
        key={`omrade-${områdeEntry.Id}-${områdeEntry.Navn}`}
        områdeEntry={områdeEntry}
        small
        omradeType={områdeData.value?.type}
        overrideSelected={valgteOmråder.value.some((v) => v.Områdekode === områdeEntry.Id)}
        onClick={async () => {
          if (områdeEntry.Id && områdeData.value?.type) {
            const alreadyAdded = valgteOmråder.value.some((v) => v.Områdekode === områdeEntry.Id);
            if (alreadyAdded) {
              valgteOmråder.set(valgteOmråder.value.filter((v) => v.Områdekode !== områdeEntry.Id));
            } else {
              valgteOmråder.set([
                ...valgteOmråder.value,
                {
                  Områdekode: områdeEntry.Id,
                  Områdetype: områdeData.value?.type,
                },
              ]);
            }
          }
        }}
      />
    );
  };

  const createPendingJaktomradeRequest = (områdekode: Områdekode) => {
    var omrade =
      appData.jaktfelt?.data?.data.find((omrade) => omrade.Id === områdekode.Områdekode) ||
      appData.villreinvald?.data?.data.find((omrade) => omrade.Id === områdekode.Områdekode) ||
      appData.vald?.data?.data.find((omrade) => omrade.Id === områdekode.Områdekode);
    if (omrade) {
      if (omrade?.JaktPåRådyr) {
        omrade = {
          ...omrade,
          JaktPåElg: false,
          JaktPåHjort: false,
        };
      }
      if (
        storage.get<Favorittområde>(
          { Områdekode: områdekode.Områdekode, OmradeType: områdekode.Områdetype },
          "PendingJaktomrade",
        )
      ) {
        storage.delete(
          { Områdekode: områdekode.Områdekode, OmradeType: områdekode.Områdetype },
          "PendingJaktomrade",
        );
      } else {
        storage.add({
          objectType: "PendingJaktomrade",
          objectKey: områdekode,
          data: { ...omrade, ...områdekode },
          isPending: true,
          operation: "create",
          lastUpdated: new Date(),
        });
      }
    } else {
      ui.showSnackbar({
        message: t("errors.unexpectedError", { details: t("errors.missingData") }),
        severity: AlertVariant.Error,
      });
    }
    appData.jaktområder.refetch();
  };

  const sendJaktområder = async () => {
    const områder = valgteOmråder.value;
    if (områder.length === 0) return [];

    isSending.set(true);
    try {
      const results = await Promise.allSettled(
        områder.map(async (områdekode) => {
          try {
            const response = await jaktdataApi.postJaktområde(områdekode);
            storage.add({
              objectType: "PendingJaktomrade",
              objectKey: {
                Områdekode: response.Områdekode || "",
                Områdetype: response.Områdetype || "",
              },
              data: { ...response },
              syncStatus: "unconfirmed",
              operation: "create",
              isPending: false,
            });

            return { ok: true, response };
          } catch (err) {
            ui.showSnackbar({
              message: t("errors.notSynchronizedYet"),
              severity: AlertVariant.Sync,
            });
            createPendingJaktomradeRequest(områdekode);
            return { ok: false, error: err };
          }
        }),
      );

      return results;
    } finally {
      isSending.set(false);
    }
  };

  return (
    <div
      data-testid="jaktomrader-testid"
      className="h-full relative flex flex-col justify-between text-ldir-brown-900"
    >
      <div>
        <div>
          <h2 className="text-2xl font-serif font-bold mt-6 m-4">
            {t("pages.jaktområder.velgArt")}
          </h2>
          <ArtButtons selected={art} />
        </div>
        {art.value !== undefined && (
          <div className="flex flex-col justify-start items-start mx-4 mt-6">
            <InputFilterDefault
              id="jaktomraderSearchFilterInput"
              ref={inputRef}
              label={`${t("pages.jaktområder.search")}${ArtType[art.value].toLowerCase()}`}
              onChange={(e) => filterValue.set(e.target.value)}
            />
            {filteredData.value && filteredData.value.length > 0 && (
              <>
                <Text className="text-ldir-brown-900 mt-6">
                  {t("pages.jaktområder.searchResult")}
                </Text>
                <BottomLine className="border-ldir-brown-900" />
              </>
            )}
            <div className="w-full overflow-y-auto">
              {filteredData.value && filteredData.value.length > 0 && (
                <>
                  {filteredData.value
                    .sort((a, b) => {
                      if (a.Navn && b.Navn) {
                        return a.Navn.localeCompare(b.Navn, i18n.language);
                      } else {
                        return 0;
                      }
                    })
                    .map((o) => {
                      return områdeEntry(o);
                    })}
                  <BottomLine className="mt-2 mb-4" />
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <BottomBarDouble
        buttonOne={
          <ButtonDefault
            icon={<CancelIcon />}
            id="jaktområderAvbrytButton"
            label={t("actions.cancel")}
            variant={ButtonVariant.TertiaryDarkBg}
            onClick={async () => {
              navigate(-1);
            }}
          />
        }
        buttonTwo={
          <ButtonDefault
            id="jaktområderLeggTilButton"
            label={t("actions.add")}
            variant={ButtonVariant.PrimaryDarkBg}
            count={valgteOmråder.value.length > 0 ? valgteOmråder.value.length : undefined}
            disabled={valgteOmråder.value.length <= 0 || isSending.value}
            onClick={async () => {
              await sendJaktområder().then(() => {
                appData.jaktområder.refetch().then(() => {
                  navigate(-1);
                });
              });
            }}
            loading={isSending.value}
          />
        }
        darkBg
      />
    </div>
  );
}
